import { Avatar, Menu, Group, useMantineTheme } from '@mantine/core';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import React, { useCallback, useMemo } from 'react';
import { IconCheck } from '@tabler/icons-react';

import { userIsStaff } from '../../utils/apiUtils';
import useEnsisQuery from '../../hooks/useEnsisQuery';
import type { Organization } from '../../types/apiTypes';

const MENU_ITEMS = [
  {
    label: 'My Profile',
    link: 'settings'
  },
  {
    label: 'Team Settings',
    link: 'settings/company-details'
  },
  {
    label: 'Log Out',
    link: 'logout'
  }
];

interface SwitchOrganizationButtonProps {
  organizations: Organization[]
  selectedOrganizationSlug: string
  onSelectOrganization: (organizationSlug: string) => void
}

const SwitchOrganizationButton: React.FC<SwitchOrganizationButtonProps> = ({
  organizations,
  selectedOrganizationSlug,
  onSelectOrganization
}: SwitchOrganizationButtonProps) => (
  <Menu.Sub disabled={organizations.length < 2}>
    <Menu.Sub.Target>
      <Menu.Sub.Item>Switch Organization</Menu.Sub.Item>
    </Menu.Sub.Target>
    <Menu.Sub.Dropdown mah={300} style={{ overflowY: 'auto' }}>
      {
        organizations.map((org) => (
          <Menu.Item
            key={org.uid}
            disabled={org.slug === selectedOrganizationSlug}
            onClick={() => { onSelectOrganization(org.slug); }}
          >
            <Group gap='xs'>
              {org.slug === selectedOrganizationSlug && <IconCheck />}
              {org.name}
            </Group>
          </Menu.Item>
        ))
      }
    </Menu.Sub.Dropdown>
  </Menu.Sub>
);

const UserAvatarMenu: React.FC = () => {
  const navigate = useNavigate();
  const { data: meData } = useEnsisQuery('/app/users/me');
  const { data: orgData } = useEnsisQuery('/app/organizations');
  const fullName = `${meData?.first_name} ${meData?.last_name}`.trim();
  const { organization } = useParams();
  const { logout } = useAuth0();
  const { colors } = useMantineTheme();

  const organizations = orgData?.items ?? [];
  const nonRevokedOrgs = organizations?.filter(
    (org) => !org.access_revoked
  ) ?? [];
  const hasMultipleOrgs = organizations.length > 1;

  const hasAdminAccess = userIsStaff(meData);

  const menuItemData = useMemo(() => {
    const menuItems = [...MENU_ITEMS];
    if (hasMultipleOrgs) {
      menuItems.splice(1, 0, {
        label: 'Switch Organization',
        link: 'switch-organization'
      });
    }
    if (hasAdminAccess) {
      menuItems.push({
        label: 'Admin Page',
        link: 'admin'
      });
    }
    return menuItems;
  }, [hasMultipleOrgs, hasAdminAccess]);

  const handleNavigateToOrg = useCallback((orgSlug: string) => {
    navigate(`/org/${orgSlug}`);
  }, [organization]);

  const menuItems = menuItemData.map((item, key) => {
    if (item.link === 'switch-organization') {
      return (
        <SwitchOrganizationButton
          key='switch-org'
          organizations={nonRevokedOrgs}
          selectedOrganizationSlug={organization ?? ''}
          onSelectOrganization={handleNavigateToOrg}
        />
      );
    }
    const onClick = () => {
      if (item.link === 'logout') {
        logout({
          logoutParams: { returnTo: window.location.origin }
        }).catch(() => { });
      } else if (item.link === 'admin') {
        navigate('/admin');
      } else {
        navigate(`/org/${organization}/${item.link}`);
      }
    };
    return (
      <Menu.Item key={+key} onClick={onClick}>
        {item.label}
      </Menu.Item>
    );
  });

  return <Menu
    offset={10}
    width={175}
    position="bottom-end"
  >
    <Menu.Target>
      <Avatar
        style={{ cursor: 'pointer' }}
        size={28}
        name={fullName}
        bg={colors.lightPurple[5]}
        color='white'
      />
    </Menu.Target>
    <Menu.Dropdown>
      {menuItems}
    </Menu.Dropdown>
  </Menu>;
};

export default UserAvatarMenu;
